import React from "react";

import { Menu, MenuDivider, MenuItem, Popover, Tooltip } from "@blueprintjs/core";
import { Completion } from "common/entities/Subscription";
import { CardInlineField } from "common/views/card/Card.view";
import IconView from "common/views/icon/Icon.view";
import { Fragment } from "core/components/FragmentProvider";
import DateLabel from "core/components/label/DateLabel";
import DurationLabel from "core/components/label/DurationLabel";
import Label from "core/components/label/Label";
import MetaLabelProvider from "./labels";
import { ISubscriptionComponentProps } from "./SubscriptionComponent";
import {IDataTableColumn} from "../../../common/views/table/DataTable";
import {ISubscriptionsFragmentProps} from "../fragments/ISubscriptionsFragmentProps";

const { Labels } = MetaLabelProvider;

export const startDate: Fragment<ISubscriptionComponentProps> = {
	name: "Start Date",
	component: ({config, subscription}) => (
		<CardInlineField>
			<IconView icon="calendar" viewKey="subscription-startDate">
				{config.showMetaLabels && [<Labels.startDate />, ': ']}
				<DateLabel value={subscription.startDate} />
			</IconView>
		</CardInlineField>
	)
};

export const endDate: Fragment<ISubscriptionComponentProps> = {
	name: "End Date",
	enabled: ({subscription}) => !!subscription.endDate,
	component: ({config, subscription}) => (
		<CardInlineField>
			<IconView icon="calendar" viewKey="subscription-endDate">
				{
					config.showMetaLabels && [<Labels.endDate />, ': ']
				}
				<DateLabel value={subscription.endDate} />
			</IconView>
		</CardInlineField>
	)
};

export const timeSpent: Fragment<ISubscriptionComponentProps> = {
	name: "Time Spent",
	enabled: ({subscription}) => !!subscription.timeSpent,
	component: ({config, subscription}) => {
		return (
			<CardInlineField>
				<IconView icon="time" viewKey="subscription-timeSpent">
					{config.showMetaLabels && [<Labels.timeSpent />, ': ']}
					<DurationLabel value={subscription.timeSpent} />
				</IconView>
			</CardInlineField>
		);
	}
};

export const daysToAccess: Fragment<ISubscriptionComponentProps> = {
	name: "Days To Acceess",
	enabled: ({subscription}) => !!subscription.endDate,
	component: ({config}) => {
		return (
			<CardInlineField>
				<IconView icon="daysToAccess" viewKey="subscription-daysToAccess">
					{config.showMetaLabels && [<Labels.daysToAccess />, ': ']}
				</IconView>
			</CardInlineField>
		);
	}
};

export const main: Fragment<ISubscriptionComponentProps> = {
	name: "main",
	component: ({config}) => {
		return (
			<CardInlineField>
				<IconView icon="main" viewKey="subscription-main">
					{config.showMetaLabels && [<Labels.main />, ': ']}
				</IconView>
			</CardInlineField>
		);
	}
};

export const score: Fragment<ISubscriptionComponentProps> = {
	name: "score",
	component: ({config}) => {
		return (
			<CardInlineField>
				<IconView icon="score" viewKey="subscription-score">
					{config.showMetaLabels && [<Labels.score />, ': ']}
				</IconView>
			</CardInlineField>
		);
	}
};

export const credits: Fragment<ISubscriptionComponentProps> = {
	name: "credits",
	component: ({config}) => {
		return (
			<CardInlineField>
				<IconView icon="credits" viewKey="subscription-credits">
					{config.showMetaLabels && [<Labels.credits />, ': ']}
				</IconView>
			</CardInlineField>
		);
	}
};

export const totalCredits:Fragment<ISubscriptionComponentProps> = {
	name: "totalCredits",
	component: ({ config}) => {

			return (
				<CardInlineField>
				<IconView icon="totalCredits" viewKey="subscription-totalCredits">
					{config.showMetaLabels && [<Labels.totalCredits />, ': ']}
				</IconView>
			</CardInlineField>
			);
		}
	};

export const ceCredits: Fragment<ISubscriptionComponentProps> = {
	name: "ceCredits",
	component: ({config}) => {
		return (
			<CardInlineField>
				<IconView icon="credits" viewKey="subscription-ce-credits">
					{config.showMetaLabels && [<Labels.credits />, ': ']}
				</IconView>
			</CardInlineField>
		);
	}
};

export const cpeCredits: Fragment<ISubscriptionComponentProps> = {
	name: "cpeCredits",
	component: ({config}) => {
		return (
			<CardInlineField>
				<IconView icon="credits" viewKey="subscription-cpe-credits">
					{config.showMetaLabels && [<Labels.credits />, ': ']}
				</IconView>
			</CardInlineField>
		);
	}
};

export const lastVisited: Fragment<ISubscriptionComponentProps> = {
	name: "Last Visited",
	enabled: ({subscription}) => !!subscription.lastVisited,
	component: ({config, subscription}) => (
		<CardInlineField>
			<IconView icon="calendar" viewKey="subscription-lastVisited">
				{
					config.showMetaLabels && [<Labels.lastVisited />, ': ']
				}
				<DateLabel value={subscription.lastVisited} />
			</IconView>
		</CardInlineField>
	)
};

export const completion: Fragment<ISubscriptionComponentProps> = {
	name: "Completion",
	enabled: ({subscription}) => subscription.completion !== Completion.NotStarted,
	component: ({subscription}) => (
		<CardInlineField>
			<React.Fragment key="completion">
				<Label k={`completion.${subscription.completion}`} v={subscription.completion} />
				{
					subscription.completedOn && [
						': ',
						<DateLabel key="date" value={subscription.completedOn} />
					]
				}
			</React.Fragment>
		</CardInlineField>
	)
};

export const completionDate: Fragment<ISubscriptionComponentProps> = {
	name: "Completion Date",
	enabled: ({subscription}) => subscription.completion !== Completion.Completed,
	component: ({subscription}) => (
		<CardInlineField>
			<React.Fragment key="completionDate">
				{
					subscription.completedOn && [
						': ',
						<DateLabel key="date" value={subscription.completedOn} />
					]
				}
			</React.Fragment>
		</CardInlineField>
	)
};
export const certificates: Fragment<ISubscriptionComponentProps> = {
	name: "Certificates",
	enabled: ({subscription}) => (!!subscription.certificates && subscription.certificates.length > 0),
	component: ({subscription}) => (
		<CardInlineField style={{cursor: 'pointer'}}>
			<Popover
				key="certificates"
				content={
					<Menu>
						<MenuDivider title={<Labels.certificates args={{count: subscription.certificates.length}}/>} />
						{
							subscription.certificates.map((cert) => (
								<MenuItem
									key={cert.id}
									href={`${window.location.origin}/diweb/catalog/cert/id/${cert.guid}/view/1`}
									target="_blank"
									text={cert.certificate && cert.certificate.name || "Untitled Certificate"}
								/>
							))
						}
					</Menu>
				}
			>
				<IconView icon="bookmark" viewKey="subscription-certificates">
					<Labels.certificates args={{count: subscription.certificates.length}}/>
				</IconView>
			</Popover>
		</CardInlineField>
	)
};

export const actions: IDataTableColumn<ISubscriptionComponentProps, any> = {
	name: "Actions",
	enabled: ({ subscription }) => !!subscription.credits && subscription.credits.length > 0,
	label: { k: "col.actions", v: "Actions" },
	component: ({ subscription }) => {
		// TO DO: TO ADD COLUMNS
	}
};


export const customField1: IDataTableColumn<ISubscriptionComponentProps, any> = {
	name: "customField1",
	sort: "customField1",
	sortable: true,
	label: {k: "col.customField1", v: "Custom Field 1"},
	component: ({subscription}) => {}
};


export const customField2: IDataTableColumn<ISubscriptionComponentProps, any> = {
	name: "customField2",
	sort: "customField2",
	sortable: true,
	label: {k: "col.customField2", v: "Custom Field 2"},
	component: ({subscription}) => {}
};


export const customField3: IDataTableColumn<ISubscriptionComponentProps, any> = {
	name: "customField3",
	sort: "customField3",
	sortable: true,
	label: {k: "col.customField3", v: "Custom Field 3"},
	component: ({subscription}) => {}
};



// WEBPACK FOOTER //
// ./src/panels/subscriptions/component/subscriptionMeta.tsx